import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings as SettingsIcon, Save, Upload, Tag, Eye, EyeOff, DollarSign, Image as ImageIcon, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WalletManagement from './WalletManagement';
import StripeConnectManagement from './StripeConnectManagement';
import './AgentSettings.css';

// Default values that are considered invalid for launch
const DEFAULT_NAME = 'My Agent';
const DEFAULT_DESCRIPTION = 'A helpful AI assistant';

const AgentSettings = () => {
  const { user, getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Agent settings state
  const [agentSettings, setAgentSettings] = useState({
    name: '',
    description: '',
    agent_avatar: 'https://example.com/avatar.png',
    tags: [],
    visibility_status: 'public',
    is_public: false,
    owner_engagement_fee_cents: 0
  });
  
  const [originalSettings, setOriginalSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);
  const visibilitySectionRef = useRef(null);
  const [highlightVisibility, setHighlightVisibility] = useState(false);
  const [isAgentLaunched, setIsAgentLaunched] = useState(false);

  // Load agent settings when component mounts
  useEffect(() => {
    if (user?.agent?.id) {
      loadAgentSettings();
    }
  }, [user?.agent?.id]);

  // Check agent readiness status to determine if agent is launched
  useEffect(() => {
    const checkAgentReadiness = async () => {
      if (!user?.agent?.id) return;
      
      try {
        const token = await getToken();
        if (!token) return;
        
        const response = await fetch('/api/agents/readiness', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAgentLaunched(data.isLaunched || false);
          
          // If agent is launched, remove highlight
          if (data.isLaunched) {
            setHighlightVisibility(false);
          }
        }
      } catch (error) {
        console.error('Error checking agent readiness:', error);
      }
    };
    
    checkAgentReadiness();
    // Check readiness periodically to detect when agent is launched
    const interval = setInterval(checkAgentReadiness, 2000);
    return () => clearInterval(interval);
  }, [user?.agent?.id, getToken]);

  // Check for highlight parameter and scroll to visibility section
  // Also highlight if agent hasn't been launched when component loads
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    const shouldHighlight = (highlight === 'visibility' || (!isAgentLaunched && user?.agent?.id));
    
    if (shouldHighlight && visibilitySectionRef.current && !isAgentLaunched) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        visibilitySectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setHighlightVisibility(true);
        // Don't remove highlight - it will be removed when agent is launched
      }, highlight === 'visibility' ? 300 : 500);
    } else if (isAgentLaunched) {
      // Remove highlight if agent is launched
      setHighlightVisibility(false);
    }
  }, [searchParams, isAgentLaunched, user?.agent?.id]);

  // Update avatar when user data becomes available
  useEffect(() => {
    if (user?.picture && (!agentSettings.agent_avatar || agentSettings.agent_avatar.includes('example.com'))) {
      setAgentSettings(prev => ({
        ...prev,
        agent_avatar: user.picture
      }));
    }
  }, [user?.picture, agentSettings.agent_avatar]);

  // Generate default avatar if none exists
  const generateDefaultAvatar = (name) => {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 200, 200);
    gradient.addColorStop(0, '#0066FF');
    gradient.addColorStop(1, '#00CCFF');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 200, 200);
    
    // Add text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 80px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.charAt(0).toUpperCase(), 100, 100);
    
    return canvas.toDataURL();
  };

  // Load agent settings from API
  const loadAgentSettings = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        setSettingsLoading(false);
        return;
      }
      
      const response = await fetch(`/api/agents/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert default values to empty strings so placeholders show
        const agentName = data.name === DEFAULT_NAME ? '' : (data.name || '');
        const agentDescription = data.description === DEFAULT_DESCRIPTION ? '' : (data.description || '');
        
        // Use Google profile picture as default if no avatar is set
        const defaultAvatar = data.agent_avatar || user?.picture || generateDefaultAvatar(agentName || DEFAULT_NAME);
        
        const loadedSettings = {
          name: agentName,
          description: agentDescription,
          agent_avatar: defaultAvatar,
          tags: data.tags || [],
          visibility_status: data.visibility_status || 'public',
          is_public: false, // Default values, will be overridden by monetization settings
          owner_engagement_fee_cents: 0
        };
        setAgentSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
        
        // Load monetization settings separately
        try {
          console.log(`🔍 [AGENT_SETTINGS] Loading monetization settings for agent ${user.agent.id}...`);
          const monetizationToken = await getToken();
          if (!monetizationToken) {
            console.error('No auth token available for monetization');
            return;
          }
          
          const monetizationResponse = await fetch(`/api/wallet/agent/${user.agent.id}/monetization`, {
            headers: {
              'Authorization': `Bearer ${monetizationToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log(`📡 [AGENT_SETTINGS] Monetization response status: ${monetizationResponse.status}`);
          
          if (monetizationResponse.ok) {
            const monetizationData = await monetizationResponse.json();
            console.log(`✅ [AGENT_SETTINGS] Received monetization data:`, monetizationData);
            const updatedSettings = {
              ...loadedSettings,
              is_public: monetizationData.is_public || false,
              owner_engagement_fee_cents: monetizationData.owner_engagement_fee_cents || 0
            };
            console.log(`🔄 [AGENT_SETTINGS] Updated settings with monetization data:`, updatedSettings);
            setAgentSettings(updatedSettings);
            setOriginalSettings({ ...updatedSettings });
          } else {
            const errorText = await monetizationResponse.text();
            console.error(`❌ [AGENT_SETTINGS] Monetization API error: ${monetizationResponse.status} - ${errorText}`);
          }
        } catch (monetizationError) {
          console.error('❌ [AGENT_SETTINGS] Error loading monetization settings:', monetizationError);
          // Continue with default values if monetization settings fail to load
        }
        
        // Clear any existing preview when loading settings
        setAvatarPreview(null);
        
        // If no avatar was saved and user has a Google picture, save it now
        if (!data.agent_avatar && user?.picture) {
          console.log('Saving Google profile picture as agent avatar');
          await saveGoogleProfilePicture(user.picture);
        }
        
        // If avatar is a Google URL, convert it to data URL
        if (data.agent_avatar && data.agent_avatar.includes('googleusercontent.com')) {
          console.log('Converting Google URL to data URL:', data.agent_avatar);
          try {
            await saveGoogleProfilePicture(data.agent_avatar);
            console.log('Google URL conversion completed, reloading settings...');
            // Reload settings after conversion
            setTimeout(() => {
              loadAgentSettings();
            }, 1000);
          } catch (error) {
            console.error('Error converting Google URL:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading agent settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Save Google profile picture as agent avatar
  const saveGoogleProfilePicture = async (googlePictureUrl) => {
    try {
      console.log('Attempting to convert and save Google profile picture:', googlePictureUrl);
      
      // Convert Google image to data URL
      const response = await fetch(googlePictureUrl);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const dataURL = e.target.result;
          
          try {
            const token = await getToken();
            if (!token) {
              console.error('No auth token available');
              reject(new Error('No auth token available'));
              return;
            }
            
            const saveResponse = await fetch(`/api/agents/settings`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                ...agentSettings,
                agent_avatar: dataURL
              })
            });
            
            if (saveResponse.ok) {
              const result = await saveResponse.json();
              console.log('Google profile picture converted and saved as agent avatar:', result);
              resolve();
            } else {
              const error = await saveResponse.json();
              console.error('Failed to save Google profile picture:', error);
              reject(new Error('Failed to save'));
            }
          } catch (error) {
            console.error('Error saving Google profile picture:', error);
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting Google profile picture:', error);
    }
  };

  // Save agent settings
  const saveAgentSettings = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsSaving(true);
    try {
      const token = await getToken();
      if (!token) {
        alert('Failed to save settings: No auth token available');
        setSettingsSaving(false);
        return;
      }
      
      // Save basic agent settings
      const response = await fetch(`/api/agents/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agentSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agent settings saved successfully:', data);
        
        // Save monetization settings separately
        if (agentSettings.is_public !== undefined || agentSettings.owner_engagement_fee_cents !== undefined) {
          console.log(`🔍 [AGENT_SETTINGS] Saving monetization settings for agent ${user.agent.id}...`);
          console.log(`📝 [AGENT_SETTINGS] Monetization data to save:`, {
            is_public: agentSettings.is_public,
            owner_engagement_fee_cents: agentSettings.owner_engagement_fee_cents
          });
          
          const monetizationToken = await getToken();
          if (!monetizationToken) {
            console.error('No auth token available for monetization');
            return;
          }
          
          const monetizationResponse = await fetch(`/api/wallet/agent/${user.agent.id}/monetization`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${monetizationToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              is_public: agentSettings.is_public,
              owner_engagement_fee_cents: agentSettings.owner_engagement_fee_cents
            })
          });
          
          console.log(`📡 [AGENT_SETTINGS] Monetization save response status: ${monetizationResponse.status}`);
          
          if (monetizationResponse.ok) {
            const responseData = await monetizationResponse.json();
            console.log('✅ [AGENT_SETTINGS] Monetization settings saved successfully:', responseData);
          } else {
            const errorText = await monetizationResponse.text();
            console.error(`❌ [AGENT_SETTINGS] Failed to save monetization settings: ${monetizationResponse.status} - ${errorText}`);
          }
        } else {
          console.log(`ℹ️ [AGENT_SETTINGS] No monetization settings to save (is_public: ${agentSettings.is_public}, owner_engagement_fee_cents: ${agentSettings.owner_engagement_fee_cents})`);
        }
        
        // Update original settings to reflect saved state
        setOriginalSettings({ ...agentSettings });
        
        // Check if agent is now launched (visibility_status === 'public')
        // This will update the highlight state
        try {
          const readinessToken = await getToken();
          if (readinessToken) {
            const readinessResponse = await fetch('/api/agents/readiness', {
              headers: {
                'Authorization': `Bearer ${readinessToken}`
              }
            });
            if (readinessResponse.ok) {
              const readinessData = await readinessResponse.json();
              setIsAgentLaunched(readinessData.isLaunched || false);
              if (readinessData.isLaunched) {
                setHighlightVisibility(false);
              }
            }
          }
        } catch (readinessError) {
          console.error('Error checking readiness after save:', readinessError);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to save agent settings:', errorData);
        alert(`Failed to save settings: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving agent settings:', error);
      alert(`Error saving settings: ${error.message}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Save only metadata (name, description, image)
  const saveMetadata = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsSaving(true);
    try {
      const token = await getToken();
      if (!token) {
        alert('Failed to save: No auth token available');
        setSettingsSaving(false);
        return;
      }
      
      // Save only metadata fields
      // Convert empty strings to defaults when saving
      const metadataToSave = {
        name: (agentSettings.name || '').trim() || DEFAULT_NAME,
        description: (agentSettings.description || '').trim() || '',
        agent_avatar: agentSettings.agent_avatar,
        tags: agentSettings.tags,
        visibility_status: agentSettings.visibility_status // Keep current visibility
      };
      
      const response = await fetch(`/api/agents/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(metadataToSave)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Metadata saved successfully:', data);
        
        // Preserve what the user actually entered - don't convert back to empty string
        // The user's input is what matters for the launch button state
        const userEnteredName = (agentSettings.name || '').trim();
        const userEnteredDescription = (agentSettings.description || '').trim();
        
        // Keep the user's input if they entered something, otherwise use what API returned
        // Only convert to empty string for display if API returned DEFAULT_NAME and user didn't enter anything
        const savedName = userEnteredName 
          ? userEnteredName  // Preserve user's input
          : (data.name === DEFAULT_NAME ? '' : data.name);
        const savedDescription = userEnteredDescription
          ? userEnteredDescription  // Preserve user's input
          : (data.description === DEFAULT_DESCRIPTION ? '' : data.description);
        
        // Update agentSettings with the saved values
        setAgentSettings(prev => {
          const updatedSettings = {
            ...prev,
            name: savedName,
            description: savedDescription,
            agent_avatar: data.agent_avatar || prev.agent_avatar
          };
          
          // Update originalSettings with the new state (use functional update to avoid stale closure)
          setOriginalSettings(updatedSettings);
          
          return updatedSettings;
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to save metadata:', errorData);
        alert(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving metadata:', error);
      alert(`Error saving: ${error.message}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Launch agent: Save all metadata + make public
  const launchAgent = async () => {
    if (!user?.agent?.id || !isReadyToLaunch()) return;
    
    setSettingsSaving(true);
    try {
      const token = await getToken();
      if (!token) {
        alert('Failed to launch: No auth token available');
        setSettingsSaving(false);
        return;
      }
      
      // Save all metadata and set visibility to public
      // Convert empty strings to defaults when saving
      const launchSettings = {
        ...agentSettings,
        name: (agentSettings.name || '').trim() || DEFAULT_NAME,
        description: (agentSettings.description || '').trim() || '',
        visibility_status: 'public'
      };
      
      const response = await fetch(`/api/agents/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(launchSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agent launched successfully:', data);
        
        // After saving, convert defaults back to empty strings for UI display (if they were defaults)
        // but keep the actual saved values in originalSettings
        const savedName = data.name === DEFAULT_NAME ? '' : data.name;
        const savedDescription = data.description === DEFAULT_DESCRIPTION ? '' : data.description;
        
        // Update agentSettings to show empty strings if defaults were saved (so placeholders show)
        // But since agent is launched, we can show the actual values
        setAgentSettings({
          ...launchSettings,
          name: savedName,
          description: savedDescription,
          agent_avatar: data.agent_avatar || launchSettings.agent_avatar
        });
        
        // Update originalSettings with actual saved values
        setOriginalSettings({
          ...launchSettings,
          name: savedName,
          description: savedDescription,
          agent_avatar: data.agent_avatar || launchSettings.agent_avatar
        });
        
        // Check readiness to update highlight state
        try {
          const readinessToken = await getToken();
          if (readinessToken) {
            const readinessResponse = await fetch('/api/agents/readiness', {
              headers: {
                'Authorization': `Bearer ${readinessToken}`
              }
            });
            if (readinessResponse.ok) {
              const readinessData = await readinessResponse.json();
              setIsAgentLaunched(readinessData.isLaunched || false);
              if (readinessData.isLaunched) {
                setHighlightVisibility(false);
              }
            }
          }
        } catch (readinessError) {
          console.error('Error checking readiness after launch:', readinessError);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to launch agent:', errorData);
        alert(`Failed to launch: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error launching agent:', error);
      alert(`Error launching: ${error.message}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Unlaunch agent: Only toggle visibility (doesn't save metadata)
  const unlaunchAgent = async () => {
    if (!user?.agent?.id) return;
    
    setSettingsSaving(true);
    try {
      const token = await getToken();
      if (!token) {
        alert('Failed to update agent: No auth token available');
        setSettingsSaving(false);
        return;
      }
      
      // Only change visibility status, don't save other metadata
      const unlaunchSettings = {
        ...agentSettings,
        visibility_status: 'private'
      };
      
      const response = await fetch(`/api/agents/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(unlaunchSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agent unlaunched successfully:', data);
        
        // Update local state
        setAgentSettings(unlaunchSettings);
        setOriginalSettings(unlaunchSettings);
        
        // Check readiness to update highlight state
        try {
          const readinessToken = await getToken();
          if (readinessToken) {
            const readinessResponse = await fetch('/api/agents/readiness', {
              headers: {
                'Authorization': `Bearer ${readinessToken}`
              }
            });
            if (readinessResponse.ok) {
              const readinessData = await readinessResponse.json();
              setIsAgentLaunched(readinessData.isLaunched || false);
            }
          }
        } catch (readinessError) {
          console.error('Error checking readiness after unlaunch:', readinessError);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to unlaunch agent:', errorData);
        alert(`Failed to update agent: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error unlaunching agent:', error);
      alert(`Error updating agent: ${error.message}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Check if metadata (name, description, image) has changed
  const hasMetadataChanges = () => {
    // If originalSettings is empty, no changes yet
    if (!originalSettings || Object.keys(originalSettings).length === 0) return false;
    
    // Normalize values for comparison (empty string = default)
    const currentName = (agentSettings.name || '').trim() || DEFAULT_NAME;
    const originalName = (originalSettings.name || '').trim() || DEFAULT_NAME;
    const currentDescription = (agentSettings.description || '').trim() || '';
    const originalDescription = (originalSettings.description || '').trim() || '';
    
    return (
      currentName !== originalName ||
      currentDescription !== originalDescription ||
      agentSettings.agent_avatar !== originalSettings.agent_avatar
    );
  };

  // Check if name and description are valid for launch (only applies before launch)
  // After launch, any name/description is allowed (including defaults)
  const isReadyToLaunch = () => {
    if (isAgentLaunched) return true; // After launch, no restrictions
    // Before launch: name must be non-empty and not just whitespace
    const name = (agentSettings.name || '').trim();
    return name.length > 0;
  };

  // Check if settings have changed (for backward compatibility)
  const hasChanges = () => {
    return JSON.stringify(agentSettings) !== JSON.stringify(originalSettings);
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setAgentSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add tag
  const addTag = () => {
    if (newTag.trim() && !agentSettings.tags.includes(newTag.trim())) {
      setAgentSettings(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setAgentSettings(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle key press for adding tags
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Handle avatar file upload
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file (PNG, JPEG, etc.)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Create preview and convert to data URL for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataURL = e.target.result;
        setAvatarPreview(dataURL);
        // Update the agent_avatar field with the data URL
        handleInputChange('agent_avatar', dataURL);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Remove avatar
  const removeAvatar = () => {
    setAvatarPreview(null);
    handleInputChange('agent_avatar', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      

      {settingsLoading ? (
        <div className="loading-state">
          <p>Loading agent settings...</p>
        </div>
      ) : (
        <div className="settings-form">
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="basic-info-container">
                <div className="text-fields">
                  <div className="form-group">
                    <label htmlFor="agent-name">Agent Name *</label>
                    <input
                      id="agent-name"
                      type="text"
                      value={agentSettings.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={DEFAULT_NAME}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="agent-description">Agent Description</label>
                    <input
                      id="agent-description"
                      type="text"
                      value={agentSettings.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder={DEFAULT_DESCRIPTION}
                      className="form-input"
                    />
                  </div>
                </div>
                
                {/* Avatar Section - Now on the right */}
                <div className="avatar-section">
                  <div className="avatar-preview-large">
                    {avatarPreview || agentSettings.agent_avatar ? (
                      <img 
                        src={avatarPreview || agentSettings.agent_avatar} 
                        alt="Agent Avatar Preview"
                        className="avatar-image-large"
                      />
                    ) : (
                      <div className="avatar-placeholder-large">
                        <ImageIcon className="avatar-icon-large" />
                      </div>
                    )}
                  </div>
                  <button 
                    type="button" 
                    onClick={triggerFileUpload}
                    className="edit-avatar-button"
                  >
                    <Upload className="edit-icon" />
                    Edit Avatar
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
              
              {/* Save Changes button - centered, only show when metadata has changed */}
              {hasMetadataChanges() && (
                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={saveMetadata}
                    disabled={settingsSaving}
                    className="save-button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--accent-primary, #0066FF)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: settingsSaving ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      opacity: settingsSaving ? 0.6 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    <Save className="button-icon" />
                    {settingsSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>


            {/* Launch Agent Section */}
            <div 
              ref={visibilitySectionRef}
              className={`form-section ${highlightVisibility ? 'highlight-section' : ''}`}
              style={highlightVisibility ? {
                border: '2px solid #0066FF',
                borderRadius: '12px',
                padding: '1.5rem',
                background: 'rgba(0, 102, 255, 0.05)',
                animation: 'pulse-highlight 2s infinite'
              } : {}}
            >
              {agentSettings.visibility_status === 'public' ? (
                // Agent is launched - show unlaunch button
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '2rem'
                }}>
                  <button
                    type="button"
                    onClick={unlaunchAgent}
                    disabled={settingsSaving}
                    className="launch-button unlaunch-button"
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: settingsSaving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      opacity: settingsSaving ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!settingsSaving) {
                        e.target.style.backgroundColor = '#b91c1c';
                        e.target.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!settingsSaving) {
                        e.target.style.backgroundColor = '#dc2626';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <Rocket size={20} />
                    {settingsSaving ? 'Updating...' : 'Unlaunch Agent'}
                  </button>
                </div>
              ) : (
                // Agent is private - show launch button
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '2rem'
                }}>
                  <p style={{
                    fontSize: '1rem',
                    color: 'var(--text-secondary, #666)',
                    textAlign: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    This makes your agent publicly visible. You can undo this action at any time.
                  </p>
                  <button
                    type="button"
                    onClick={launchAgent}
                    disabled={settingsSaving || !isReadyToLaunch()}
                    className="launch-button"
                    style={{
                      backgroundColor: isReadyToLaunch() ? '#10b981' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: (settingsSaving || !isReadyToLaunch()) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      opacity: settingsSaving ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!settingsSaving && isReadyToLaunch()) {
                        e.target.style.backgroundColor = '#059669';
                        e.target.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!settingsSaving && isReadyToLaunch()) {
                        e.target.style.backgroundColor = '#10b981';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <Rocket size={20} />
                    {settingsSaving ? 'Launching...' : isReadyToLaunch() ? 'Launch Agent' : 'Complete setup to launch'}
                  </button>
                </div>
              )}
            </div>

            {/* Hide Stripe-related sections in production */}
            {process.env.NODE_ENV !== 'production' && (
              <>
                {/* Monetization */}
                <div className="form-section">
                  <h3>Monetization Settings</h3>
              <p className="section-description">
                Set up your agent for the marketplace. Users get 2 free messages, then pay OpenAI costs + 10%. Optionally, set a fee for your personal engagement.
              </p>
                  
                  <div className="form-group">
                    <label htmlFor="is-public">
                      <input
                        id="is-public"
                        type="checkbox"
                        checked={agentSettings.is_public || false}
                        onChange={(e) => handleInputChange('is_public', e.target.checked)}
                      />
                      <span>Make this agent public in the marketplace</span>
                    </label>
                    <p className="field-description">
                      When enabled, users can discover and chat with your agent on the /talkto page.
                    </p>
                  </div>
                  
                  {agentSettings.is_public && (
                    <div className="form-group">
                      <label htmlFor="owner-engagement-fee">Owner engagement fee (USD)</label>
                      <div className="price-input-container">
                        <input
                          id="owner-engagement-fee"
                          type="number"
                          value={agentSettings.owner_engagement_fee_cents ? (agentSettings.owner_engagement_fee_cents / 100) : ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            handleInputChange('owner_engagement_fee_cents', value * 100);
                          }}
                          placeholder="0"
                          min="0"
                          max="100"
                          step="1"
                        />
                        <span className="currency-suffix">USD</span>
                      </div>
                      <p className="field-description">
                        Optional: Set a fee for users to request your personal review and engagement in the conversation (max $100).
                      </p>
                    </div>
                  )}
                </div>

                {/* Wallet & Billing */}
                <div className="form-section">
                  <h3>Wallet & Billing</h3>
                  <p className="section-description">
                    Manage your account balance and billing information. As an agent owner, you can receive payments and manage your earnings.
                  </p>
                  <WalletManagement />
                </div>

                {/* Stripe Connect Account */}
                <div className="form-section">
                  <h3>Payment Account</h3>
                  <p className="section-description">
                    Set up your Stripe Connect account to receive payments from agent interactions. You'll earn 85% of the revenue from your agents.
                  </p>
                  <StripeConnectManagement />
                </div>
              </>
            )}
        </div>
      )}
    </div>
  );
};

export default AgentSettings;
